import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const RewardsContext = createContext();

// Backend API URL
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useRewards = () => {
  const context = useContext(RewardsContext);
  if (!context) {
    throw new Error('useRewards must be used within a RewardsProvider');
  }
  return context;
};

// Virtual prizes kids can earn
const VIRTUAL_PRIZES = [
  // Stickers (cheap - 15-50 points)
  { id: 'sticker-star', name: 'Gold Star', emoji: '⭐', cost: 20, category: 'stickers', description: 'A shiny gold star!' },
  { id: 'sticker-heart', name: 'Love Heart', emoji: '❤️', cost: 20, category: 'stickers', description: 'Show some love!' },
  { id: 'sticker-rainbow', name: 'Rainbow', emoji: '🌈', cost: 25, category: 'stickers', description: 'Colorful rainbow!' },
  { id: 'sticker-rocket', name: 'Rocket Ship', emoji: '🚀', cost: 30, category: 'stickers', description: 'Blast off!' },
  { id: 'sticker-unicorn', name: 'Unicorn', emoji: '🦄', cost: 35, category: 'stickers', description: 'Magical unicorn!' },
  { id: 'sticker-crown', name: 'Crown', emoji: '👑', cost: 40, category: 'stickers', description: 'Feel like royalty!' },
  { id: 'sticker-fire', name: 'Fire', emoji: '🔥', cost: 15, category: 'stickers', description: 'You\'re on fire!' },
  { id: 'sticker-lightning', name: 'Lightning', emoji: '⚡', cost: 20, category: 'stickers', description: 'Super fast!' },
  { id: 'sticker-sparkle', name: 'Sparkles', emoji: '✨', cost: 15, category: 'stickers', description: 'Sparkle and shine!' },
  { id: 'sticker-moon', name: 'Moon', emoji: '🌙', cost: 25, category: 'stickers', description: 'Dreamy moon!' },
  { id: 'sticker-sun', name: 'Sunshine', emoji: '☀️', cost: 20, category: 'stickers', description: 'Bright like the sun!' },
  { id: 'sticker-flower', name: 'Flower', emoji: '🌸', cost: 20, category: 'stickers', description: 'Pretty flower!' },
  { id: 'sticker-butterfly', name: 'Butterfly', emoji: '🦋', cost: 30, category: 'stickers', description: 'Beautiful wings!' },
  { id: 'sticker-balloon', name: 'Balloon', emoji: '🎈', cost: 15, category: 'stickers', description: 'Party time!' },
  { id: 'sticker-candy', name: 'Candy', emoji: '🍬', cost: 20, category: 'stickers', description: 'Sweet treat!' },
  { id: 'sticker-icecream', name: 'Ice Cream', emoji: '🍦', cost: 25, category: 'stickers', description: 'Yummy!' },
  { id: 'sticker-pizza', name: 'Pizza', emoji: '🍕', cost: 25, category: 'stickers', description: 'Delicious!' },
  { id: 'sticker-cookie', name: 'Cookie', emoji: '🍪', cost: 20, category: 'stickers', description: 'Smart cookie!' },
  { id: 'sticker-cupcake', name: 'Cupcake', emoji: '🧁', cost: 25, category: 'stickers', description: 'Sweet reward!' },
  { id: 'sticker-donut', name: 'Donut', emoji: '🍩', cost: 20, category: 'stickers', description: 'Donut worry, be happy!' },
  
  // Pets (medium - 80-200 points)
  { id: 'pet-cat', name: 'Kitty Cat', emoji: '🐱', cost: 100, category: 'pets', description: 'A cute virtual kitty!' },
  { id: 'pet-dog', name: 'Puppy Dog', emoji: '🐶', cost: 100, category: 'pets', description: 'Your loyal friend!' },
  { id: 'pet-bunny', name: 'Bunny Rabbit', emoji: '🐰', cost: 120, category: 'pets', description: 'Hop hop hop!' },
  { id: 'pet-panda', name: 'Baby Panda', emoji: '🐼', cost: 150, category: 'pets', description: 'So cuddly!' },
  { id: 'pet-dragon', name: 'Baby Dragon', emoji: '🐉', cost: 200, category: 'pets', description: 'Magical dragon friend!' },
  { id: 'pet-dino', name: 'Dino Friend', emoji: '🦖', cost: 200, category: 'pets', description: 'Prehistoric pal!' },
  { id: 'pet-hamster', name: 'Hamster', emoji: '🐹', cost: 80, category: 'pets', description: 'Tiny and cute!' },
  { id: 'pet-fox', name: 'Baby Fox', emoji: '🦊', cost: 120, category: 'pets', description: 'Clever little fox!' },
  { id: 'pet-koala', name: 'Koala Bear', emoji: '🐨', cost: 130, category: 'pets', description: 'Sleepy koala!' },
  { id: 'pet-lion', name: 'Lion Cub', emoji: '🦁', cost: 180, category: 'pets', description: 'King of the jungle!' },
  { id: 'pet-tiger', name: 'Tiger Cub', emoji: '🐯', cost: 180, category: 'pets', description: 'Fierce and cute!' },
  { id: 'pet-bear', name: 'Teddy Bear', emoji: '🧸', cost: 90, category: 'pets', description: 'Cuddly teddy!' },
  { id: 'pet-penguin', name: 'Penguin', emoji: '🐧', cost: 110, category: 'pets', description: 'Waddle waddle!' },
  { id: 'pet-owl', name: 'Wise Owl', emoji: '🦉', cost: 140, category: 'pets', description: 'So wise!' },
  { id: 'pet-dolphin', name: 'Dolphin', emoji: '🐬', cost: 160, category: 'pets', description: 'Ocean friend!' },
  { id: 'pet-whale', name: 'Baby Whale', emoji: '🐳', cost: 170, category: 'pets', description: 'Gentle giant!' },
  { id: 'pet-octopus', name: 'Octopus', emoji: '🐙', cost: 150, category: 'pets', description: '8 arms to hug!' },
  { id: 'pet-turtle', name: 'Turtle', emoji: '🐢', cost: 100, category: 'pets', description: 'Slow and steady!' },
  { id: 'pet-frog', name: 'Froggy', emoji: '🐸', cost: 90, category: 'pets', description: 'Ribbit ribbit!' },
  { id: 'pet-butterfly', name: 'Butterfly Pet', emoji: '🦋', cost: 120, category: 'pets', description: 'Flutter flutter!' },
  
  // Badges (achievements - 60-200 points)
  { id: 'badge-brain', name: 'Super Brain', emoji: '🧠', cost: 75, category: 'badges', description: 'You\'re so smart!' },
  { id: 'badge-champion', name: 'Champion', emoji: '🏆', cost: 100, category: 'badges', description: 'A true champion!' },
  { id: 'badge-scientist', name: 'Little Scientist', emoji: '🔬', cost: 100, category: 'badges', description: 'Science explorer!' },
  { id: 'badge-artist', name: 'Creative Artist', emoji: '🎨', cost: 100, category: 'badges', description: 'So creative!' },
  { id: 'badge-hero', name: 'Learning Hero', emoji: '🦸', cost: 150, category: 'badges', description: 'Hero of learning!' },
  { id: 'badge-wizard', name: 'Math Wizard', emoji: '🧙', cost: 150, category: 'badges', description: 'Math magic!' },
  { id: 'badge-star', name: 'Superstar', emoji: '🌟', cost: 80, category: 'badges', description: 'You\'re a star!' },
  { id: 'badge-rocket', name: 'Rocket Learner', emoji: '🚀', cost: 90, category: 'badges', description: 'Learning so fast!' },
  { id: 'badge-book', name: 'Bookworm', emoji: '📚', cost: 70, category: 'badges', description: 'Love to read!' },
  { id: 'badge-music', name: 'Music Lover', emoji: '🎵', cost: 80, category: 'badges', description: 'Musical genius!' },
  { id: 'badge-globe', name: 'World Explorer', emoji: '🌍', cost: 120, category: 'badges', description: 'Explore the world!' },
  { id: 'badge-detective', name: 'Detective', emoji: '🔍', cost: 100, category: 'badges', description: 'Problem solver!' },
  { id: 'badge-ninja', name: 'Learning Ninja', emoji: '🥷', cost: 130, category: 'badges', description: 'Stealthy learner!' },
  { id: 'badge-robot', name: 'Robot Friend', emoji: '🤖', cost: 110, category: 'badges', description: 'Tech genius!' },
  { id: 'badge-alien', name: 'Space Cadet', emoji: '👽', cost: 120, category: 'badges', description: 'Out of this world!' },
  { id: 'badge-dino', name: 'Dino Expert', emoji: '🦕', cost: 100, category: 'badges', description: 'Dinosaur lover!' },
  { id: 'badge-nature', name: 'Nature Friend', emoji: '🌿', cost: 80, category: 'badges', description: 'Love nature!' },
  { id: 'badge-sports', name: 'Sports Star', emoji: '⚽', cost: 90, category: 'badges', description: 'Athletic achiever!' },
  { id: 'badge-chef', name: 'Little Chef', emoji: '👨‍🍳', cost: 100, category: 'badges', description: 'Cooking skills!' },
  { id: 'badge-pilot', name: 'Sky Pilot', emoji: '✈️', cost: 140, category: 'badges', description: 'Flying high!' },
  
  // Special Items (expensive - 200-1000 points)
  { id: 'special-treasure', name: 'Treasure Chest', emoji: '💎', cost: 300, category: 'special', description: 'Full of gems!' },
  { id: 'special-medal', name: 'Gold Medal', emoji: '🥇', cost: 250, category: 'special', description: 'First place!' },
  { id: 'special-castle', name: 'Magic Castle', emoji: '🏰', cost: 500, category: 'special', description: 'Your own castle!' },
  { id: 'special-spaceship', name: 'Spaceship', emoji: '🛸', cost: 500, category: 'special', description: 'Explore space!' },
  { id: 'special-rainbow', name: 'Rainbow World', emoji: '🌈', cost: 400, category: 'special', description: 'All the colors!' },
  { id: 'special-planet', name: 'Own Planet', emoji: '🪐', cost: 600, category: 'special', description: 'Your own planet!' },
  { id: 'special-submarine', name: 'Submarine', emoji: '🚢', cost: 450, category: 'special', description: 'Explore the deep!' },
  { id: 'special-helicopter', name: 'Helicopter', emoji: '🚁', cost: 400, category: 'special', description: 'Fly anywhere!' },
  { id: 'special-racecar', name: 'Race Car', emoji: '🏎️', cost: 350, category: 'special', description: 'Super fast!' },
  { id: 'special-train', name: 'Magic Train', emoji: '🚂', cost: 300, category: 'special', description: 'Choo choo!' },
  { id: 'special-ferriswheel', name: 'Ferris Wheel', emoji: '🎡', cost: 450, category: 'special', description: 'Theme park fun!' },
  { id: 'special-rollercoaster', name: 'Roller Coaster', emoji: '🎢', cost: 500, category: 'special', description: 'Thrilling ride!' },
  { id: 'special-circus', name: 'Circus Tent', emoji: '🎪', cost: 550, category: 'special', description: 'Join the circus!' },
  { id: 'special-trophy', name: 'Ultimate Trophy', emoji: '🏅', cost: 800, category: 'special', description: 'The biggest prize!' },
  { id: 'special-crown', name: 'Royal Crown', emoji: '👸', cost: 700, category: 'special', description: 'Rule your kingdom!' },
  { id: 'special-wand', name: 'Magic Wand', emoji: '🪄', cost: 600, category: 'special', description: 'Cast spells!' },
  { id: 'special-crystal', name: 'Crystal Ball', emoji: '🔮', cost: 650, category: 'special', description: 'See the future!' },
  { id: 'special-volcano', name: 'Volcano Island', emoji: '🌋', cost: 750, category: 'special', description: 'Explosive fun!' },
  { id: 'special-aurora', name: 'Northern Lights', emoji: '🌌', cost: 900, category: 'special', description: 'Magical sky!' },
  { id: 'special-universe', name: 'Own Universe', emoji: '🌠', cost: 1000, category: 'special', description: 'Create your world!' },
];

// Achievement milestones
const ACHIEVEMENTS = [
  { id: 'first-quiz', name: 'First Quiz!', emoji: '🎯', description: 'Complete your first quiz', requirement: { type: 'quizzes', count: 1 }, reward: 25 },
  { id: 'quiz-master-5', name: 'Quiz Explorer', emoji: '🌟', description: 'Complete 5 quizzes', requirement: { type: 'quizzes', count: 5 }, reward: 50 },
  { id: 'quiz-master-10', name: 'Quiz Champion', emoji: '🏆', description: 'Complete 10 quizzes', requirement: { type: 'quizzes', count: 10 }, reward: 100 },
  { id: 'quiz-master-25', name: 'Quiz Legend', emoji: '👑', description: 'Complete 25 quizzes', requirement: { type: 'quizzes', count: 25 }, reward: 200 },
  { id: 'correct-10', name: 'Smart Cookie', emoji: '🍪', description: 'Get 10 correct answers', requirement: { type: 'correct', count: 10 }, reward: 30 },
  { id: 'correct-50', name: 'Brainiac', emoji: '🧠', description: 'Get 50 correct answers', requirement: { type: 'correct', count: 50 }, reward: 75 },
  { id: 'correct-100', name: 'Genius!', emoji: '💡', description: 'Get 100 correct answers', requirement: { type: 'correct', count: 100 }, reward: 150 },
  { id: 'streak-3', name: 'On Fire!', emoji: '🔥', description: '3 day learning streak', requirement: { type: 'streak', count: 3 }, reward: 50 },
  { id: 'streak-7', name: 'Week Warrior', emoji: '⚔️', description: '7 day learning streak', requirement: { type: 'streak', count: 7 }, reward: 100 },
  { id: 'collector-5', name: 'Collector', emoji: '🎁', description: 'Collect 5 prizes', requirement: { type: 'prizes', count: 5 }, reward: 50 },
  { id: 'points-100', name: 'Point Hunter', emoji: '💰', description: 'Earn 100 points', requirement: { type: 'totalPoints', count: 100 }, reward: 25 },
  { id: 'points-500', name: 'Point Master', emoji: '💎', description: 'Earn 500 points', requirement: { type: 'totalPoints', count: 500 }, reward: 75 },
  { id: 'points-1000', name: 'Point Legend', emoji: '🌟', description: 'Earn 1000 points', requirement: { type: 'totalPoints', count: 1000 }, reward: 150 },
];

// Daily challenges
const DAILY_CHALLENGES = [
  { id: 'daily-quiz', name: 'Complete a Quiz', emoji: '📝', description: 'Finish any quiz today', type: 'quiz', target: 1, reward: 15 },
  { id: 'daily-correct', name: 'Get 5 Right', emoji: '✅', description: 'Answer 5 questions correctly', type: 'correct', target: 5, reward: 15 },
  { id: 'daily-memory', name: 'Memory Master', emoji: '🎴', description: 'Play a memory game', type: 'memory', target: 1, reward: 15 },
  { id: 'daily-sorting', name: 'Sort it Out', emoji: '📦', description: 'Complete a sorting game', type: 'sorting', target: 1, reward: 15 },
  { id: 'daily-story', name: 'Story Time', emoji: '📖', description: 'Read a story', type: 'story', target: 1, reward: 15 },
];

export const RewardsProvider = ({ children }) => {
  // Load saved data from localStorage (used as fallback/cache)
  const loadFromStorage = (key, defaultValue) => {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const [points, setPoints] = useState(() => loadFromStorage('rewards_points', 0));
  const [totalPointsEarned, setTotalPointsEarned] = useState(() => loadFromStorage('rewards_total', 0));
  const [ownedPrizes, setOwnedPrizes] = useState(() => loadFromStorage('rewards_prizes', []));
  const [unlockedAchievements, setUnlockedAchievements] = useState(() => loadFromStorage('rewards_achievements', []));
  const [stats, setStats] = useState(() => loadFromStorage('rewards_stats', {
    quizzesCompleted: 0,
    correctAnswers: 0,
    gamesPlayed: 0,
    lessonsCompleted: 0,
    currentStreak: 0,
    lastPlayDate: null,
  }));
  const [dailyProgress, setDailyProgress] = useState(() => loadFromStorage('rewards_daily', {
    date: new Date().toDateString(),
    challenges: {},
    completed: [],
  }));
  const [weeklyGoal, setWeeklyGoal] = useState(() => loadFromStorage('rewards_weekly', {
    weekStart: getWeekStart(),
    lessonsTarget: 10,
    lessonsCompleted: 0,
    claimed: false,
  }));
  const [newAchievement, setNewAchievement] = useState(null);
  const [showRewardAnimation, setShowRewardAnimation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSyncedPlayer, setLastSyncedPlayer] = useState(null);

  // Get current player name from localStorage
  const getPlayerName = () => localStorage.getItem('playerName') || '';

  // Load rewards from backend for a player
  const loadRewardsFromBackend = useCallback(async (playerName) => {
    if (!playerName) return;
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/rewards/${encodeURIComponent(playerName)}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.exists) {
          // Load data from backend
          setPoints(data.points || 0);
          setTotalPointsEarned(data.total_points_earned || 0);
          setOwnedPrizes(data.owned_prizes || []);
          setUnlockedAchievements(data.unlocked_achievements || []);
          setStats(data.stats || {
            quizzesCompleted: 0,
            correctAnswers: 0,
            gamesPlayed: 0,
            lessonsCompleted: 0,
            currentStreak: 0,
            lastPlayDate: null,
          });
          setDailyProgress(data.daily_progress || {
            date: new Date().toDateString(),
            challenges: {},
            completed: [],
          });
          setWeeklyGoal(data.weekly_goal || {
            weekStart: getWeekStart(),
            lessonsTarget: 10,
            lessonsCompleted: 0,
            claimed: false,
          });
          
          // Also save to localStorage as cache
          localStorage.setItem('rewards_points', JSON.stringify(data.points || 0));
          localStorage.setItem('rewards_total', JSON.stringify(data.total_points_earned || 0));
          localStorage.setItem('rewards_prizes', JSON.stringify(data.owned_prizes || []));
          localStorage.setItem('rewards_achievements', JSON.stringify(data.unlocked_achievements || []));
          localStorage.setItem('rewards_stats', JSON.stringify(data.stats || {}));
          localStorage.setItem('rewards_daily', JSON.stringify(data.daily_progress || {}));
          localStorage.setItem('rewards_weekly', JSON.stringify(data.weekly_goal || {}));
          
          console.log(`Loaded rewards for ${playerName} from backend`);
        }
        setLastSyncedPlayer(playerName);
      }
    } catch (error) {
      console.error('Error loading rewards from backend:', error);
      // Fall back to localStorage data (already loaded)
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save rewards to backend
  const saveRewardsToBackend = useCallback(async () => {
    const playerName = getPlayerName();
    if (!playerName) return;
    
    try {
      await fetch(`${API_URL}/api/rewards/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_name: playerName,
          points,
          total_points_earned: totalPointsEarned,
          owned_prizes: ownedPrizes,
          unlocked_achievements: unlockedAchievements,
          stats,
          daily_progress: dailyProgress,
          weekly_goal: weeklyGoal,
        }),
      });
      console.log(`Saved rewards for ${playerName} to backend`);
    } catch (error) {
      console.error('Error saving rewards to backend:', error);
    }
  }, [points, totalPointsEarned, ownedPrizes, unlockedAchievements, stats, dailyProgress, weeklyGoal]);

  // Load rewards when player name changes
  useEffect(() => {
    const playerName = getPlayerName();
    if (playerName && playerName !== lastSyncedPlayer) {
      loadRewardsFromBackend(playerName);
    }
  }, [loadRewardsFromBackend, lastSyncedPlayer]);

  // Listen for player name changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'playerName' && e.newValue) {
        loadRewardsFromBackend(e.newValue);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadRewardsFromBackend]);

  // Check for player name on mount and periodically
  useEffect(() => {
    const checkPlayer = () => {
      const playerName = getPlayerName();
      if (playerName && playerName !== lastSyncedPlayer) {
        loadRewardsFromBackend(playerName);
      }
    };
    
    checkPlayer();
    const interval = setInterval(checkPlayer, 2000); // Check every 2 seconds
    return () => clearInterval(interval);
  }, [loadRewardsFromBackend, lastSyncedPlayer]);

  // Save to localStorage when state changes (local cache)
  useEffect(() => {
    localStorage.setItem('rewards_points', JSON.stringify(points));
  }, [points]);

  useEffect(() => {
    localStorage.setItem('rewards_total', JSON.stringify(totalPointsEarned));
  }, [totalPointsEarned]);

  useEffect(() => {
    localStorage.setItem('rewards_prizes', JSON.stringify(ownedPrizes));
  }, [ownedPrizes]);

  useEffect(() => {
    localStorage.setItem('rewards_achievements', JSON.stringify(unlockedAchievements));
  }, [unlockedAchievements]);

  useEffect(() => {
    localStorage.setItem('rewards_stats', JSON.stringify(stats));
  }, [stats]);

  useEffect(() => {
    localStorage.setItem('rewards_daily', JSON.stringify(dailyProgress));
  }, [dailyProgress]);

  useEffect(() => {
    localStorage.setItem('rewards_weekly', JSON.stringify(weeklyGoal));
  }, [weeklyGoal]);

  // Auto-save to backend when important data changes (debounced)
  useEffect(() => {
    const playerName = getPlayerName();
    if (!playerName) return;
    
    const timeoutId = setTimeout(() => {
      saveRewardsToBackend();
    }, 1000); // Debounce: save 1 second after last change
    
    return () => clearTimeout(timeoutId);
  }, [points, totalPointsEarned, ownedPrizes, unlockedAchievements, stats, saveRewardsToBackend]);

  // Check for new day
  useEffect(() => {
    const today = new Date().toDateString();
    if (dailyProgress.date !== today) {
      // New day - reset daily challenges
      setDailyProgress({
        date: today,
        challenges: {},
        completed: [],
      });
      
      // Update streak
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      if (stats.lastPlayDate === yesterday.toDateString()) {
        setStats(prev => ({ ...prev, currentStreak: prev.currentStreak + 1 }));
      } else if (stats.lastPlayDate !== today) {
        setStats(prev => ({ ...prev, currentStreak: 1 }));
      }
    }
  }, []);

  // Check weekly goal reset
  useEffect(() => {
    const currentWeekStart = getWeekStart();
    if (weeklyGoal.weekStart !== currentWeekStart) {
      setWeeklyGoal({
        weekStart: currentWeekStart,
        lessonsTarget: 10,
        lessonsCompleted: 0,
        claimed: false,
      });
    }
  }, []);

  function getWeekStart() {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day;
    return new Date(now.setDate(diff)).toDateString();
  }

  // Add points with animation
  const addPoints = (amount, source = 'activity') => {
    setPoints(prev => prev + amount);
    setTotalPointsEarned(prev => prev + amount);
    setShowRewardAnimation({ points: amount, source });
    setTimeout(() => setShowRewardAnimation(null), 2000);
    // Only check achievements for non-achievement point sources to prevent infinite loop
    if (source !== 'achievement') {
      checkAchievements();
    }
  };

  // Award for correct answer (+2 points)
  const awardCorrectAnswer = () => {
    addPoints(2, 'correct');
    setStats(prev => ({ 
      ...prev, 
      correctAnswers: prev.correctAnswers + 1,
      lastPlayDate: new Date().toDateString()
    }));
    updateDailyProgress('correct', 1);
  };

  // Award for completing a lesson/game (+10 points)
  const awardLessonComplete = (gameType = 'quiz') => {
    addPoints(10, 'lesson');
    setStats(prev => ({ 
      ...prev, 
      lessonsCompleted: prev.lessonsCompleted + 1,
      quizzesCompleted: gameType === 'quiz' ? prev.quizzesCompleted + 1 : prev.quizzesCompleted,
      gamesPlayed: prev.gamesPlayed + 1,
      lastPlayDate: new Date().toDateString()
    }));
    updateDailyProgress(gameType, 1);
    
    // Update weekly goal
    setWeeklyGoal(prev => ({
      ...prev,
      lessonsCompleted: prev.lessonsCompleted + 1
    }));
  };

  // Update daily challenge progress
  const updateDailyProgress = (type, amount) => {
    setDailyProgress(prev => {
      const newChallenges = { ...prev.challenges };
      newChallenges[type] = (newChallenges[type] || 0) + amount;
      
      // Check if any daily challenges completed
      const newCompleted = [...prev.completed];
      DAILY_CHALLENGES.forEach(challenge => {
        if (!newCompleted.includes(challenge.id)) {
          const progress = newChallenges[challenge.type] || 0;
          if (progress >= challenge.target) {
            newCompleted.push(challenge.id);
            // Award daily challenge points
            setTimeout(() => addPoints(15, 'daily'), 500);
          }
        }
      });
      
      return { ...prev, challenges: newChallenges, completed: newCompleted };
    });
  };

  // Claim weekly goal reward (+50 points)
  const claimWeeklyGoal = () => {
    if (weeklyGoal.lessonsCompleted >= weeklyGoal.lessonsTarget && !weeklyGoal.claimed) {
      addPoints(50, 'weekly');
      setWeeklyGoal(prev => ({ ...prev, claimed: true }));
      return true;
    }
    return false;
  };

  // Check and unlock achievements
  const checkAchievements = () => {
    ACHIEVEMENTS.forEach(achievement => {
      if (!unlockedAchievements.includes(achievement.id)) {
        let progress = 0;
        switch (achievement.requirement.type) {
          case 'quizzes':
            progress = stats.quizzesCompleted;
            break;
          case 'correct':
            progress = stats.correctAnswers;
            break;
          case 'streak':
            progress = stats.currentStreak;
            break;
          case 'prizes':
            progress = ownedPrizes.length;
            break;
          case 'totalPoints':
            progress = totalPointsEarned;
            break;
          default:
            break;
        }
        
        if (progress >= achievement.requirement.count) {
          setUnlockedAchievements(prev => [...prev, achievement.id]);
          setNewAchievement(achievement);
          setTimeout(() => addPoints(achievement.reward, 'achievement'), 1000);
          setTimeout(() => setNewAchievement(null), 4000);
        }
      }
    });
  };

  // Buy a prize
  const buyPrize = (prizeId) => {
    const prize = VIRTUAL_PRIZES.find(p => p.id === prizeId);
    if (prize && points >= prize.cost && !ownedPrizes.includes(prizeId)) {
      setPoints(prev => prev - prize.cost);
      setOwnedPrizes(prev => [...prev, prizeId]);
      return true;
    }
    return false;
  };

  // Get today's daily challenges with progress
  const getDailyChallenges = () => {
    return DAILY_CHALLENGES.map(challenge => ({
      ...challenge,
      progress: dailyProgress.challenges[challenge.type] || 0,
      completed: dailyProgress.completed.includes(challenge.id),
    }));
  };

  const value = {
    points,
    totalPointsEarned,
    ownedPrizes,
    unlockedAchievements,
    stats,
    dailyProgress,
    weeklyGoal,
    newAchievement,
    showRewardAnimation,
    isLoading,
    addPoints,
    awardCorrectAnswer,
    awardLessonComplete,
    claimWeeklyGoal,
    buyPrize,
    getDailyChallenges,
    loadRewardsFromBackend,
    saveRewardsToBackend,
    VIRTUAL_PRIZES,
    ACHIEVEMENTS,
  };

  return (
    <RewardsContext.Provider value={value}>
      {children}
    </RewardsContext.Provider>
  );
};

export default RewardsContext;
